/**
 * ResultPanel v0.2
 *
 * 結果が出ると画面に重なる全画面モーダル。要件:
 *   - ORIGINAL（緑薄背景・緑枠）+ ▼ ORIGINAL ヘッダー
 *   - JAPANESE（オレンジ薄背景・オレンジ枠）+ ▼ JAPANESE ヘッダー
 *   - メタ情報行：CONF（信頼度%）/ LANG（src→ja）
 *   - HISTORY 直近3件のプレビュー
 *
 * v0.1 互換のテキストフォールバック対応：
 *   - latestResult.original/japanese が無くても、text を JAPANESE 側に表示
 *
 * 状態：
 *   - status === 'idle' かつ result===null → 何も表示しない（カメラだけ見える）
 *   - status === 'processing' → ANALYZING… パルス
 *   - status === 'error' → 赤字メッセージ
 *   - status === 'done' || result !== null → 構造化結果を表示
 */

import { useEffect, useState } from 'react';

import type { Mode, ScanResult } from '../types';
import type { ScannerStatus } from '../hooks/useScanner';

import styles from './ResultPanel.module.css';

export interface ResultPanelProps {
  status: ScannerStatus;
  result: ScanResult | null;
  errorMessage: string | null;
  currentMode: Mode;
  /** 直近の履歴。先頭3件だけ HISTORY セクションに表示 */
  history?: ScanResult[];
  /**
   * SCAN中（連続認識中）の場合のみ渡される STOP ハンドラ。
   * 渡されているときだけ ANALYZING… 行に「⏹ STOP」ボタンを出す。
   * 押すと SCAN が完全停止し、ANALYZING… は STAND BY（status idle）へ。
   * モーダル自体は × で閉じるまで残る。
   */
  onStop?: () => void;
}

const MODE_LABEL: Record<Mode, string> = {
  translate: 'TRANSLATION',
  summarize: 'SUMMARY',
  interpret: 'INTERPRET',
};

/** ISO文字列を「HH:MM:SS」形式に整形 */
function formatTime(iso: string): string {
  const date = new Date(iso);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

/** "en" "fr" 等を「EN→JA」表示に整形 */
function formatLang(sourceLang: string | undefined): string {
  if (!sourceLang || sourceLang === 'unknown') return '— → JA';
  if (sourceLang === 'ja') return 'JA';
  return `${sourceLang.toUpperCase()} → JA`;
}

export function ResultPanel({
  status,
  result,
  errorMessage,
  currentMode,
  history = [],
  onStop,
}: ResultPanelProps) {
  // ユーザーが × で閉じた後に再度開かれるためのフラグ。
  // 新しい結果が到着したら自動的に再表示する。
  const [closedId, setClosedId] = useState<string | null>(null);
  useEffect(() => {
    if (result && result.id !== closedId) {
      // 新しい結果が来たら closed フラグをリセット
      setClosedId(null);
    }
  }, [result, closedId]);

  // 表示条件：処理中 / エラー / 結果がある時のみ
  const hasResult = result !== null && result.id !== closedId;
  const isVisible =
    status === 'processing' || status === 'error' || hasResult;

  if (!isVisible) return null;

  const handleClose = () => {
    if (result) setClosedId(result.id);
  };

  // 構造化フィールドを取り出す（フォールバックあり）
  const originalText = result?.original ?? '';
  const japaneseText = result?.japanese ?? result?.text ?? '';
  const confidence = result?.confidence;
  const sourceLang = result?.sourceLang;

  // HISTORY セクション：現在表示中の result.id は除外して直近3件
  const historyItems = result
    ? history.filter((h) => h.id !== result.id).slice(0, 3)
    : history.slice(0, 3);

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="result-title">
      <div className={styles.dialog}>
        <div className={styles.header}>
          <h2 id="result-title" className={styles.title}>
            {MODE_LABEL[result?.mode ?? currentMode]}
            {result && ` · ${formatTime(result.timestamp)}`}
          </h2>
          {status !== 'processing' && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="結果を閉じる"
            >
              ×
            </button>
          )}
        </div>

        {/* === 処理中 === */}
        {status === 'processing' && (
          <div className={styles.processing}>
            <span className={styles.processingDot} aria-hidden />
            <span className={styles.processingLabel}>ANALYZING…</span>
            {/* SCAN中だけ STOP ボタンを出す（手動SHOTでは出さない） */}
            {onStop && (
              <button
                type="button"
                className={styles.stopButton}
                onClick={onStop}
                aria-label="連続認識を停止"
              >
                ⏹ STOP
              </button>
            )}
          </div>
        )}

        {/* === エラー === */}
        {status === 'error' && errorMessage && (
          <p className={styles.errorMessage}>{errorMessage}</p>
        )}

        {/* === 結果 === */}
        {hasResult && result && (
          <>
            {/* ORIGINAL */}
            {originalText && (
              <section className={`${styles.block} ${styles.blockOriginal}`}>
                <span className={styles.blockHeader}>▼ ORIGINAL</span>
                <p className={styles.blockBody}>{originalText}</p>
              </section>
            )}

            {/* JAPANESE */}
            {japaneseText && (
              <section className={`${styles.block} ${styles.blockJapanese}`}>
                <span className={styles.blockHeader}>▼ JAPANESE</span>
                <p className={styles.blockBody}>{japaneseText}</p>
              </section>
            )}

            {/* META */}
            <div className={styles.meta}>
              <div className={styles.metaCell}>
                <span className={styles.metaLabel}>CONF</span>
                <span className={styles.metaValue}>
                  {confidence !== undefined ? `${confidence}%` : '—'}
                </span>
              </div>
              <div className={styles.metaCell}>
                <span className={styles.metaLabel}>LANG</span>
                <span className={styles.metaValue}>{formatLang(sourceLang)}</span>
              </div>
            </div>

            {/* HISTORY */}
            {historyItems.length > 0 && (
              <section className={styles.history} aria-label="直近の履歴">
                <h3 className={styles.historyTitle}>▼ HISTORY</h3>
                {historyItems.map((entry) => (
                  <div key={entry.id} className={styles.historyItem}>
                    <span className={styles.historyItemMeta}>
                      {formatTime(entry.timestamp)} · {MODE_LABEL[entry.mode]}
                    </span>
                    <p className={styles.historyItemText}>
                      {entry.japanese ?? entry.text}
                    </p>
                  </div>
                ))}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
